import type MailFolderTableQuery from '../../store/schema/MailFolderTableQuery';
import type SortBy from '../../store/schema/SortBy';
import { type SortColumn } from '../../store/schema/SortBy';
import type TableQuery from '../../store/schema/TableQuery';
import { type TableQueryType } from '../../store/schema/TableQuery';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import type SortDirection from 'owa-service/lib/contract/SortDirection';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import type { MailboxInfo } from 'owa-client-types';
import { isServiceRequestSupportedForMailbox } from 'owa-service/lib/utils/isServiceRequestSupportedForMailbox';
import { isPrioritizeOnDeliveryOn } from 'owa-copilot-settings-utils';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import folderStore, { isFolderOrSubFolderOfDistinguishedFolder } from 'owa-folders';

export const DESCENDING_SORT_DIR: SortDirection = 'Descending';
export const ASCENDING_SORT_DIR: SortDirection = 'Ascending';
export const DEFAULT_SORT_COLUMN: SortColumn = 1;

/**
 * Gets the SortBy that supports Pin
 */
export function getSortBySupportingPin(): SortBy {
    return {
        sortColumn: 1,
        sortDirection: DESCENDING_SORT_DIR,
    };
}

/**
 * Gets the list of supported sort columns
 * @param folderId for which to return the sort columns
 */
export function getSupportedSortColumns(
    folderId: string,
    mailboxInfo: MailboxInfo,
    isConversationView: boolean = true
): SortColumn[] {
    const supportedSortColumns: SortColumn[] = [1];

    if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
        return supportedSortColumns;
    }

    if (isPrioritizeOnDeliveryOn(mailboxInfo)) {
        supportedSortColumns.push(16);
    }

    if (folderIdToName(folderId, true /* shouldSearchShared */) === 'scheduled') {
        supportedSortColumns.push(15);
    }

    const folder = folderStore().folderTable.get(folderId);
    const shouldShowToSort = !!(
        folder &&
        (isFolderOrSubFolderOfDistinguishedFolder(folder, 'sentitems') ||
            isFolderOrSubFolderOfDistinguishedFolder(folder, 'drafts'))
    );

    if (shouldShowToSort) {
        supportedSortColumns.push(9);
    } else {
        supportedSortColumns.push(3);
    }

    // TODO: Remove the PST check once the PST resolver is fixed for Category sort.
    // Bug 298969: Sort by Category fails with error
    if (isServiceRequestSupportedForMailbox(mailboxInfo)) {
        supportedSortColumns.push(14);
    }

    // Flagged is not a valid sort for item view yet, so only add for conversation
    // view. Should be removed when/if item view supports Flagged sort.
    if (isConversationView) {
        supportedSortColumns.push(2);
    }

    supportedSortColumns.push(7, 5, 8, 6);

    return supportedSortColumns;
}

/**
 * Gets the default sortDirection for the given sortColumn
 * @param sortColumn sortColumn for which to get the default sort direction
 * @returns the sortDirection
 */
export function getDefaultSortDirectionForSortColumn(sortColumn: SortColumn): SortDirection {
    switch (sortColumn) {
        case 16:
        case 1:
        case 7:
        case 5:
        case 14:
        case 2:
            return DESCENDING_SORT_DIR;

        case 3:
        case 9:
        case 8:
        case 15:
        case 6:
            return ASCENDING_SORT_DIR;

        default:
            throw new Error('getDefaultSortDirectionForSortColumn: sortColumn not supported');
    }
}

/**
 * Gets the default sortBy property
 * @returns returns the default sortBy property
 */
export function getDefaultSortBy(): SortBy {
    return {
        sortColumn: DEFAULT_SORT_COLUMN,
        sortDirection: getDefaultSortDirectionForSortColumn(DEFAULT_SORT_COLUMN),
    };
}

/**
 * Gets a flag indicating whether the given tableQuery is multi-value sorted
 * @param tableQuery tableQuery to check if multi-value sorted
 * @return flag indicating whether the given tableQuery is multi-value sorted
 */
export function isTableMultiValueSort(tableQuery: TableQuery): boolean {
    const tableSortColumn =
        tableQuery.type == 0 && (tableQuery as MailFolderTableQuery).sortBy.sortColumn;
    return tableSortColumn && tableQuery.listViewType == 0 && tableSortColumn == 3;
}
